import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef
} from "react";

interface UpdateAvailableNotificationContextType {
  showUpdateIcon: boolean;
  setShowUpdateIcon: (show: boolean) => void;
  onUpdateIconHover: () => void;
  onUpdateIconClick: () => void;
  onUpdateIconMouseLeave: () => void;
  setUpdateIconHandlers: (handlers: {
    onHover: () => void;
    onClick: () => void;
    onMouseLeave: () => void;
  }) => void;
}

const UpdateAvailableNotificationContext =
  createContext<UpdateAvailableNotificationContextType | undefined>(undefined);

interface Props {
  children: ReactNode;
}

export const UpdateAvailableNotificationProvider = ({ children }: Props) => {
  const [showUpdateIcon, setShowUpdateIcon] = useState(false);
  const handlersRef = useRef<{
    onHover: () => void;
    onClick: () => void;
    onMouseLeave: () => void;
  }>({
    onHover: () => { /* no-op */ },
    onClick: () => { /* no-op */ },
    onMouseLeave: () => { /* no-op */ },
  });

  const setUpdateIconHandlers = (newHandlers: {
    onHover: () => void;
    onClick: () => void;
    onMouseLeave: () => void;
  }) => {
    handlersRef.current = newHandlers;
  };

  const onUpdateIconHover = () => {
    handlersRef.current.onHover();
  };

  const onUpdateIconClick = () => {
    handlersRef.current.onClick();
  };

  const onUpdateIconMouseLeave = () => {
    handlersRef.current.onMouseLeave();
  };

  return (
    <UpdateAvailableNotificationContext.Provider
      value={{
        showUpdateIcon,
        setShowUpdateIcon,
        onUpdateIconHover,
        onUpdateIconClick,
        onUpdateIconMouseLeave,
        setUpdateIconHandlers,
      }}
    >
      {children}
    </UpdateAvailableNotificationContext.Provider>
  );
};

export const useUpdateAvailableNotification = () => {
  const context = useContext(UpdateAvailableNotificationContext);
  if (context === undefined) {
    throw new Error("useUpdateAvailableNotification must be used within an UpdateAvailableNotificationProvider");
  }
  return context;
};
